import { amountToString, newAmount } from '@orus.eu/amount'
import { nbsp } from '@orus.eu/char'

import type { DeductiblePart, InsuranceDeductible, PossibleInsuranceDeductible } from '../../../model/policy-item.ts'
import { getFrMrpaxeGeneralDeductible, getFrMrpaxeWorksLiabilityDeductible } from '../fr-mrpaxe-derogations.ts'
import type { FrMrpaxePolicyItem, FrMrpaxePolicyItemsState } from '../fr-mrpaxe-insurance.ts'

type MultipleDeductibles = Extract<InsuranceDeductible, { type: 'multipleDeductibles' }>

type FrMrpaxeDeductibleEntry = { additionalInfo: string | null } & (
  | { deductible: PossibleInsuranceDeductible; select?: never }
  | { select: (state: FrMrpaxePolicyItemsState) => InsuranceDeductible; deductible?: never }
)

/**
 * A 2,000 € deductible replaces the usual one for every claim of the first month of the contract. It applies to the
 * damage covers and the options except business interruption (PE), not to the liability covers or assistance.
 */
const firstMonth: DeductiblePart = {
  label: 'Pendant le premier mois du contrat, pour tous les sinistres',
  deductible: { type: 'fixedAmount', value: newAmount(2_000) },
}

function generalDeductible(state: FrMrpaxePolicyItemsState): DeductiblePart {
  return {
    label: 'Cas général',
    deductible: {
      type: 'fixedAmount',
      value: getFrMrpaxeGeneralDeductible(state.frMrpaxeDerogationGeneralDeductible ?? undefined),
    },
  }
}

function generalDeductibleThenFirstMonth(state: FrMrpaxePolicyItemsState): MultipleDeductibles {
  return { type: 'multipleDeductibles', deductibles: [generalDeductible(state), firstMonth] }
}

function fixedThenFirstMonth(amount: number): MultipleDeductibles {
  return {
    type: 'multipleDeductibles',
    deductibles: [{ label: 'Cas général', deductible: { type: 'fixedAmount', value: newAmount(amount) } }, firstMonth],
  }
}

function textThenFirstMonth(text: string): MultipleDeductibles {
  return {
    type: 'multipleDeductibles',
    deductibles: [{ label: 'Cas général', deductible: { type: 'text', value: text } }, firstMonth],
  }
}

function amountText(amount: number): string {
  return amountToString(newAmount(amount), { addCurrency: true, displayDecimals: false })
}

/** The works liability deductible of the agreed terms, for damages capped at `maximum`. */
function worksLiabilityDeductible(state: FrMrpaxePolicyItemsState, maximum: number): DeductiblePart['deductible'] {
  const deductible = getFrMrpaxeWorksLiabilityDeductible(
    getFrMrpaxeGeneralDeductible(state.frMrpaxeDerogationGeneralDeductible ?? undefined),
    newAmount(maximum),
  )
  switch (deductible.type) {
    case 'tenPercentOfDamages':
      return {
        type: 'text',
        value: `10${nbsp}% des dommages avec un minimum de ${amountToString(deductible.minimum, { addCurrency: true, displayDecimals: false })} et un maximum de ${amountText(maximum)}`,
      }
    case 'generalDeductible':
      return { type: 'fixedAmount', value: deductible.amount }
  }
}

/**
 * The deductible of each policy item. The general deductible is 450 €, or the underwriter's derogation. PVV and the
 * works liability take the general deductible, and the broken glass deductible is 80 €.
 */
export const frMrpaxeDeductibleByPolicyItem_v1_0_0 = {
  Incendie: {
    select: (state) => ({
      type: 'multipleDeductibles',
      deductibles: [
        generalDeductible(state),
        { label: 'Mobilier extérieur', deductible: { type: 'fixedAmount', value: newAmount(500) } },
        firstMonth,
      ],
    }),
    additionalInfo: null,
  },
  DommagesElec: { select: generalDeductibleThenFirstMonth, additionalInfo: null },
  DegatsEaux: { select: generalDeductibleThenFirstMonth, additionalInfo: null },
  Vol: { select: generalDeductibleThenFirstMonth, additionalInfo: null },
  Vandalisme: {
    select: (state) => {
      if (state.frMrpaxeDerogationGeneralDeductible && state.frMrpaxeDerogationGeneralDeductible !== 'DEFAULT') {
        return generalDeductibleThenFirstMonth(state)
      }
      const general = amountToString(getFrMrpaxeGeneralDeductible(undefined), {
        addCurrency: true,
        displayDecimals: false,
      })
      return textThenFirstMonth(`De ${general} à ${amountText(1_000)}`)
    },
    additionalInfo: null,
  },
  EvenementsClim: {
    select: (state) => ({
      type: 'multipleDeductibles',
      deductibles: [{ ...generalDeductible(state), label: 'Minimum' }, firstMonth],
    }),
    additionalInfo: null,
  },
  CatastropheNat: {
    deductible: textThenFirstMonth(
      `10${nbsp}% des dommages avec un minimum de 1 140${nbsp}€ et de 3 050${nbsp}€ pour la sécheresse-réhydratation`,
    ),
    additionalInfo: 'Franchise légale, fixée par arrêté interministériel',
  },
  Attentat: { select: generalDeductibleThenFirstMonth, additionalInfo: null },
  MarchConservee: { select: generalDeductibleThenFirstMonth, additionalInfo: null },
  BrisMateriel: { select: generalDeductibleThenFirstMonth, additionalInfo: null },
  RespCivOccupant: {
    select: (state) => generalDeductible(state).deductible,
    additionalInfo: null,
  },
  publicLiability: {
    select: (state) => ({
      type: 'multipleDeductibles',
      deductibles: [
        {
          label: 'Cas général',
          deductible: {
            type: 'text',
            value: `${amountToString(getFrMrpaxeGeneralDeductible(state.frMrpaxeDerogationGeneralDeductible ?? undefined), { addCurrency: true, displayDecimals: false })} minimum`,
          },
        },
        { label: 'Dommages corporels', deductible: { type: 'none' } },
      ],
    }),
    additionalInfo: null,
  },
  PE: {
    deductible: { type: 'text', value: 'Pas de jour de carence, mais minimum 3 jours de sinistre' },
    additionalInfo: null,
  },
  BDG: { deductible: fixedThenFirstMonth(80), additionalInfo: null },
  PVV: { select: generalDeductibleThenFirstMonth, additionalInfo: null },
  RCT: {
    select: (state) => ({
      type: 'multipleDeductibles',
      deductibles: [
        {
          label: 'Dommages matériels et immatériels consécutifs',
          deductible: worksLiabilityDeductible(state, 750),
        },
        {
          label: 'Atteinte accidentelle à l’environnement',
          deductible: worksLiabilityDeductible(state, 1_500),
        },
        { label: 'Dommages corporels', deductible: { type: 'none' } },
        firstMonth,
      ],
    }),
    additionalInfo: null,
  },
  BOOST: { deductible: textThenFirstMonth('Voir les garanties applicables'), additionalInfo: null },
  assistance: { deductible: { type: 'none' }, additionalInfo: null },
} as const satisfies Record<FrMrpaxePolicyItem, FrMrpaxeDeductibleEntry>
